import {
  Component,
  Renderer2,
  ElementRef,
  OnDestroy,
  AfterViewInit,
  NgZone
} from '@angular/core';
import { CmsBannerComponent, CmsService, OccConfig } from '@spartacus/core';
import { CmsComponentData } from '@spartacus/storefront';
import { Observable, Subscription, fromEvent, merge } from 'rxjs';
import { UserInfoService } from '../../spartacus/services/user-info.service';
import { debounceTime } from 'rxjs/operators';

@Component({
    selector: 'app-fluad-rwe-first',
    templateUrl: './fluad-rwe-first.component.html',
    styleUrls: ['./fluad-rwe-first.component.scss'],
    standalone: false
})
export class FluadRweFirstComponent implements AfterViewInit, OnDestroy {
  userLoggedIn: boolean = false;
  userInfo: any;
  webEnabled: any;
  firstnode: Observable<any> = this.bannerComponent.data$;

  private io?: IntersectionObserver;
  private subs = new Subscription();

  // play guards
  private hasPlayed = false;

  // hero root
  private hostEl?: HTMLElement;

  constructor(
    private cmsService: CmsService,
    private el: ElementRef<HTMLElement>,
    protected config: OccConfig,
    public bannerComponent: CmsComponentData<CmsBannerComponent>,
    private userInfoService: UserInfoService,
    private renderer: Renderer2,
    private ngZone: NgZone
  ) {}

  ngAfterViewInit(): void {
    // keep your existing login logic intact
    this.subs.add(
      this.userInfoService.userLoggedIn$.subscribe((res) => {
        this.userLoggedIn = res;
        if (this.userLoggedIn) {
          this.webEnabled = localStorage.getItem('webEnabled');
        }
      })
    );

    if (this.userInfo?.webEnabled == false) {
      this.renderer.setStyle(document.querySelector('.SideContent'), 'display', 'none');
    }

    // SSR guard
    if (typeof window === 'undefined') return;

    // hero root
    this.hostEl = this.el.nativeElement.querySelector('.fluad-hero') as HTMLElement | null || undefined;
    if (!this.hostEl) return;

    // ensure we do NOT animate on load
    this.hostEl.classList.remove('is-animating');

    // arm IO for first-time play-once in viewport
    this.armObserver();

    // -------------------------------
    // Resolution-change auto trigger
    // -------------------------------
    // We listen to: window resize, orientationchange, visualViewport resize (when available).
    this.ngZone.runOutsideAngular(() => {
      const resize$ = fromEvent(window, 'resize');
      const orient$ = fromEvent(window, 'orientationchange');
      const vvp$    = (window.visualViewport
        ? fromEvent(window.visualViewport, 'resize')
        : fromEvent(document, 'visibilitychange')
      );
    
      this.subs.add(
        merge(resize$, orient$, vvp$)
          .pipe(debounceTime(120))
          .subscribe(() => {
            const vv = (window as any).visualViewport;
            const w = vv?.width ?? window.innerWidth;
    
            // ✅ Only run when width > 500px
            if (w > 500) {
              this.ngZone.run(() => this.onResolutionChanged());
            }
          })
      );
    });
    
  }

  ngOnDestroy(): void {
    this.teardownObserver();
    this.subs.unsubscribe();
  }

  // --------------- IO helpers ---------------
  private armObserver(): void {
    if (!this.hostEl) return;
    this.io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && !this.hasPlayed) {
            this.playOnce();
            break;
          }
        }
      },
      { threshold: 0.5 }
    );
    this.io.observe(this.hostEl);
  }

  private teardownObserver(): void {
    this.io?.disconnect();
    this.io = undefined;
  }

  private playOnce(): void {
    if (!this.hostEl) return;
    this.hasPlayed = true;
    this.restartAnimation(); // toggles class to start CSS sequence
    // stop IO for this mode/view
    this.io?.unobserve(this.hostEl);
    this.teardownObserver();
  }

  private restartAnimation(): void {
    if (!this.hostEl) return;
    this.hostEl.classList.remove('is-animating');
    void this.hostEl.offsetWidth; // reflow to reset CSS animations
    this.hostEl.classList.add('is-animating');
  }

  private isHeroRoughlyInView(): boolean {
    if (!this.hostEl) return false;
    const rect = this.hostEl.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    // Check hero mid-point is within viewport height (≈50% in view heuristic)
    const mid = rect.top + rect.height / 2;
    return mid >= 0 && mid <= vh;
  }

  // --------------- Resolution change handler ---------------
  private onResolutionChanged(): void {
    if (!this.hostEl) return;

    // If hero is currently visible, restart animation.
    // If not visible, re-arm IO so it will start once the hero re-enters viewport.
    if (this.isHeroRoughlyInView()) {
      // allow replay on resolution change regardless of previous play
      this.hasPlayed = false;  // re-enable single play
      this.restartAnimation();
      // we don’t need IO for this immediate replay
      this.teardownObserver();
    } else {
      // re-arm IO for next viewport entry
      this.hasPlayed = false;  // next entry will play again
      this.teardownObserver();
      this.armObserver();
    }
  }

  // (kept from your original)
  ngoninit() {}

  getImage(image: any): string {
    return this.config.backend?.occ?.baseUrl + image.url;
  }
}